import { useMemo } from 'react';
import { Field, FieldTypes, validatePhoneNumber, validateUrl } from '@common-fe/common-fe';
import * as yup from 'yup';

import { EMAIL_INVALID_TEXT, FieldStateKey, PermissionKey } from '@/common/constants';
import regexp from '@/common/regexp';
import { FeatureFlags, useFeatureFlag } from '@/modules/featureFlags';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

import { useHideFields } from '../hooks';
import { HelpInfoPayload } from '../SetupEmployer.types';

interface Props {
  isEmployerPage: boolean;
  viewMode: boolean;
  customerServiceNumber?: string;
  customerServiceUrlName?: string;
  customerServiceUrlLink?: string;
  customerServiceEmail?: string;
  customerServiceIsEnabled?: boolean;
  enableParticipantChatbot?: boolean;
  participantChatbotLink?: string;
  enableAdminChatbot?: boolean;
  adminChatbotLink?: string;
  zendeskSsoEnabled?: boolean;
  workingHours?: string;
}

export const useHelpSectionFields = (
  props: Props,
  triggeredId?: string,
  helpInfoState?: HelpInfoPayload | null
) => {
  const isChatbotZendeskSsoEnabled = useFeatureFlag(FeatureFlags.CHATBOT_ZENDESK_SSO);

  const customerServiceEmailPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.CUSTOMER_SERVICE_EMAIL,
    FieldStateKey.CUSTOMER_SERVICE_EMAIL
  );
  const customerServiceNumberPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.CUSTOMER_SERVICE_NUMBER,
    FieldStateKey.CUSTOMER_SERVICE_NUMBER
  );
  const customerServiceUrlLinkPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.CUSTOMER_SERVICE_URL_LINK,
    FieldStateKey.CUSTOMER_SERVICE_URL_LINK
  );
  const customerServiceUrlNamePermProps = useGetPermissionToEditOverridableField(
    PermissionKey.CUSTOMER_SERVICE_URL_NAME,
    FieldStateKey.CUSTOMER_SERVICE_URL_NAME
  );
  const customerServiceIsEnabledPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.IS_CUSTOMER_SERVICE_ENABLED,
    FieldStateKey.IS_CUSTOMER_SERVICE_ENABLED
  );
  const participantChatbotLinkPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.PARTICIPANT_CHATBOT_LINK,
    FieldStateKey.PARTICIPANT_CHATBOT_LINK
  );
  const adminChatbotLinkPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.ADMIN_CHATBOT_LINK,
    FieldStateKey.ADMIN_CHATBOT_LINK
  );
  const workingHoursPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.WORKING_HOURS,
    FieldStateKey.WORKING_HOURS
  );
  const zendeskSsoEnabledPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.ZENDESK_SSO_ENABLED,
    FieldStateKey.ZENDESK_SSO_ENABLED
  );

  const enableParticipantChatbot = helpInfoState?.enableParticipantChatbot;
  const enableAdminChatbot = helpInfoState?.enableAdminChatbot;

  const fields = useMemo(
    () => [
      {
        name: 'customerServiceIsEnabled',
        type: FieldTypes.Checkbox,
        testId: 'customerServiceIsEnabled',
        label: 'Enable help',
        ...(typeof props?.customerServiceIsEnabled === 'boolean'
          ? {
              value: props?.customerServiceIsEnabled,
              defaultValue: props?.customerServiceIsEnabled,
            }
          : {}),
        disabled: props.viewMode || props.isEmployerPage,
        ...customerServiceIsEnabledPermProps,
      },
      {
        name: 'workingHours',
        type: FieldTypes.Text,
        testId: 'workingHours',
        label: 'Working hours',
        placeholder: 'Working hours',
        disabled: props.viewMode,
        value: props?.workingHours,
        defaultValue: props?.workingHours,
         maxLength: 50,
        ...workingHoursPermProps,
      },
      {
        name: 'customerServiceNumber',
        type: FieldTypes.PhoneNumber,
        testId: 'customerServiceNumber',
        label: 'Help phone number',
        placeholder: '+1 (555) 555-1234',
        defaultValue: props?.customerServiceNumber,
        disabled: props.viewMode || props.isEmployerPage,
        ...(props.viewMode || props.isEmployerPage|| customerServiceNumberPermProps?.disabled
          ? {}
          : {
              validator: yup.string().test(validatePhoneNumber('Phone number must be valid')),
            }),
        ...customerServiceNumberPermProps,
      },
      {
        name: 'customerServiceUrlName',
        type: FieldTypes.Text,
        testId: 'customerServiceUrlName',
        label: 'Help URL name',
        placeholder: 'URL name',
        defaultValue: props?.customerServiceUrlName,
        value: props?.customerServiceUrlName,
        disabled: props.viewMode || props.isEmployerPage,
        ...customerServiceUrlNamePermProps,
      },
      {
        name: 'customerServiceUrlLink',
        type: FieldTypes.Text,
        testId: 'customerServiceUrlLink',
        label: 'Help URL',
        placeholder: 'URL',
        defaultValue: props?.customerServiceUrlLink,
        value: props?.customerServiceUrlLink,
        disabled: props.viewMode || props.isEmployerPage,
        ...(props.viewMode || props.isEmployerPage || customerServiceUrlLinkPermProps.disabled
          ? {}
          : {
              validator: yup.string().test(validateUrl('URL must be valid')),
            }),
        ...customerServiceUrlLinkPermProps,
      },
      {
        name: 'customerServiceEmail',
        type: FieldTypes.Text,
        testId: 'customerServiceEmail',
        label: 'Help email',
        placeholder: 'Help email',
        defaultValue: props?.customerServiceEmail,
        disabled: props.viewMode || props.isEmployerPage,
        ...(props.viewMode || props.isEmployerPage || customerServiceEmailPermProps?.disabled
          ? {}
          : {
              validator: yup.string().test({
                test: (val) => {
                  if (val) return regexp.EMAIL.test(val);
                  return true;
                },
                message: EMAIL_INVALID_TEXT,
              }),
            }),
        ...customerServiceEmailPermProps,
      },
      {
        name: 'enableParticipantChatbot',
        type: FieldTypes.Checkbox,
        testId: 'enableParticipantChatbot',
        label: 'Enable participant Zendesk chatbot',
        disabled: props.viewMode,
        value: props?.enableParticipantChatbot,
        defaultValue: props?.enableParticipantChatbot,
        ...participantChatbotLinkPermProps,
      },
      ...(enableParticipantChatbot
        ? [
            {
              name: 'participantChatbotLink',
              type: FieldTypes.Text,
              testId: 'participantChatbotLink',
              label: 'Participant Zendesk chatbot key',
              placeholder: 'Participant Zendesk chatbot key',
              disabled: props.viewMode,
              defaultValue: props?.participantChatbotLink,
              value: props?.participantChatbotLink,
              ...participantChatbotLinkPermProps,
            },
          ]
        : []),
        ...(helpInfoState?.participantChatbotLink && isChatbotZendeskSsoEnabled
          ? [
              {
                name: 'zendeskSsoEnabled',
                type: FieldTypes.Checkbox,
                testId: 'zendeskSsoEnabled',
                label: 'Zendesk SSO',
                disabled: props.viewMode,
                value: props?.zendeskSsoEnabled,
                defaultValue: props?.zendeskSsoEnabled,
                ...zendeskSsoEnabledPermProps,
              },
            ]
          : []
        ),
        {
        name: 'enableAdminChatbot',
        type: FieldTypes.Checkbox,
        testId: 'enableAdminChatbot',
        label: 'Enable admin Zendesk chatbot',
        disabled: props.viewMode,
        value: props?.enableAdminChatbot,
        defaultValue: props?.enableAdminChatbot,
        ...adminChatbotLinkPermProps,
      },
      ...(enableAdminChatbot
        ? [
            {
              name: 'adminChatbotLink',
              type: FieldTypes.Text,
              testId: 'adminChatbotLink',
              label: 'Admin Zendesk chatbot key',
              placeholder: 'Admin Zendesk chatbot key',
              disabled: props.viewMode,
              defaultValue: props?.adminChatbotLink,
              value: props?.adminChatbotLink,
              ...adminChatbotLinkPermProps,
            },
          ]
        : []),
    ],
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
      props,
      customerServiceEmailPermProps,
      customerServiceNumberPermProps,
      customerServiceUrlLinkPermProps,
      customerServiceUrlNamePermProps,
      customerServiceIsEnabledPermProps,
      participantChatbotLinkPermProps,
      adminChatbotLinkPermProps,
      enableParticipantChatbot,
      enableAdminChatbot,
      triggeredId,
      isChatbotZendeskSsoEnabled,
    ]
  ) as Field[];

  const formattedFields = useHideFields(fields);
  return formattedFields;
};
